// @flow
import _ from 'lodash';
import moment from 'moment';

export const LEAGUE_SIZE = _.range(4, 19, 2).map(value => ({ value, text: value }));
export const DRAFT_TURN_LENGTH = [
	{
		value: 30,
		text: '30 seconds',
	},
	{
		value: 60,
		text: '60 seconds',
	},
	{
		value: 120,
		text: '2 minutes',
	},
	{
		value: 300,
		text: '5 minutes',
	},
	{
		value: 900,
		text: '15 minutes',
	},
	{
		value: 3600,
		text: '60 minutes',
	},
	{
		value: 43200,
		text: '12 hours',
	}
];

const FINAL_ROUNDS = {
	'8': 4,
	'6': 3,
	'4': 2,
	'2': 1,
	'0': 0
};
type Props = {
	league_size: number,
};

export const getLeagueFinalFormat = ({ league_size }: Props) => {
	let max_users = league_size > 8 ? 8 : league_size;

	return _.rangeRight(2, ++max_users, 2)
		.map(value => ({ value, text: `TOP ${value}` }));
};


type FinalProps = {
	selected_round_id: number;
	finals_format: number;
	finals_start_round: number;
	rounds: Array<{ value: number, text: string }>;
	field_name: string;
};

const getNewFinalStartRound = (rounds, selected_round, start_round_id) => {
	if (!rounds.length) {
		return selected_round + 1;
	}
	const first_id = _.first(rounds).value;
	const last_id = _.last(rounds).value;
	const is_in_range = _.inRange(selected_round, first_id - 1, last_id + 1);

	return is_in_range && selected_round > start_round_id ? selected_round : last_id - 1;
};

export const getLeagueFinalsRounds = ({
	selected_round_id, rounds, finals_format, finals_start_round, field_name
}: FinalProps) => {
	// console.log(selected_round_id, 'SEL');
	// console.log(rounds, 'rounds');
	// console.log(finals_format, 'format');
	// console.log(finals_start_round, 'start');
	// console.log(field_name, 'field');


	if (!rounds.length || !selected_round_id) {
		return {
			final_rounds: [],
			finals_start_round_selected: 0
		};
	}

	const final_round_id = parseInt(finals_start_round, 10);

	const form_names = ['finals_format', 'start_round'];
	const is_need_to_change_selected_final = form_names.includes(field_name);

	const final_rounds: Array<{ value: number, text: string }> = rounds.slice(0).filter(
		({ value }) => {
			return value > selected_round_id;
		}
	);


	const round_to_splice = -FINAL_ROUNDS[finals_format] + 1;

	if (round_to_splice) {
		final_rounds.splice(round_to_splice);
	}
	// Default round should be 24 round - finals round. Not the last one available;
	const final_allowed_length =
		final_rounds.length === 1 ? final_rounds.length - 1 : final_rounds.length - 2;

	const default_round = _.get(final_rounds[final_allowed_length], 'value', 21);
	const selected_final_round = is_need_to_change_selected_final ? (
		getNewFinalStartRound(final_rounds, final_round_id, selected_round_id)
	) : final_round_id;

	return {
		final_rounds: final_rounds,
		finals_start_round_selected: final_round_id !== 0 ? selected_final_round : default_round
	};
};

export const BYE_ROUNDS = [12, 13, 15, 16, 18, 19];

export const SQUAD_SIZE = [
	{
		value: '1-2-2-1-1-2/8',
		text: '1-2-2-1-1-2/8',
	},
	{
		value: '1-2-2-1-1-2/5',
		text: '1-2-2-1-1-2/5',
	},
	{
		value: '1-1-1-1-1-1/6',
		text: '1-1-1-1-1-1/6',
	},
	{
		value: '1-3-2-2-2-3/4',
		text: '1-3-2-2-2-3/4',
	},
	{
		value: '1-3-2-2-2-3/12',
		text: '1-3-2-2-2-3/12',
	},
];

export const getStartDraftDate = () => {
	// const
	// 	start_date = moment.unix(process.env.REACT_APP_START_DRAFT_DATE),
	// 	now_date = new Date(),
	// 	future_start_date = moment(now_date.setMinutes(now_date.getMinutes() + 30));

	// return start_date.unix() >= future_start_date.unix() ? start_date : future_start_date;

	//F2P1-77611  - BE ticket to set earliest Draft date & time to be Tuesday Feb 3, 2025 @ 1pm AEDT
	const start_date = moment.unix(Number(process.env.REACT_APP_START_DRAFT_DATE));

	if (start_date.isAfter(moment())) {
		return start_date;
	}

	return start_date;

};

export const league_tooltips = {
	name: {
		title: 'LEAGUE NAME',
		description: 'Max of 40 characters. ' +
			'Inappropriate league names will be ' +
			'deleted and replaced with your team name.',
	},
	size: {
		title: 'LEAGUE SIZE',
		description: 'Your league size needs to have ' +
			'an even number of teams from 4 - 20 and work in ' +
			'conjunction with other league settings to be valid'
	},
	start_round: {
		title: 'LEAGUE START ROUND',
		description: 'This can be changed ' +
			'to select any round as your preferred ' +
			'start round provided it is a valid round ' +
			'based on other configurable league settings',
	},
	privacy: {
		title: 'PRIVACY SETTINGS',
		description: 'Private leagues can only be joined ' +
			'via invite or league code. Public leagues are available ' +
			'for anyone to join.',
	},
	draft_start: {
		title: 'Draft Date & Time',
		description: 'Times are in local time. Choose what date and time the Draft will commence.',
	},
	draft_type: {
		title: 'DRAFT TYPE',
		description: 'Live drafts involve players utilising real-time strategy and reactionary ' +
			'measures to acquire specific available players. Auto-drafts use preliminary draft ' +
			'rankings set by each User to automate the draft cycle and establish the teams.',
	},
	draft_order: {
		title: 'DRAFT ORDER',
		description: 'You can choose either Snake or Linear Draft Order. ' +
			'The Snake Draft Order means the player who has the 1st pick in odd Rounds (1,3,5 etc..) ' +
			'of the draft will have the last pick in even rounds (2,4,6 etc...). ' +
			'The Linear Draft order means that the order that is established for the first round is ' +
			'then carried on through the following rounds. ' +
			'That means if you have the first pick in round 1, ' +
			'then you will have first pick for the following rounds too.',
	},
	draft_turn: {
		title: 'Draft Turn Time',
		description: 'As the commissioner of the league you ' +
			'are able to choose how long each Team has to make their ' +
			'pick during the draft. If a team takes longer than the ' +
			'allotted time their pick will be automatically made for them ' +
			'based on their pre-draft list.',
	},
	finals: {
		title: 'LEAGUE FINAL SERIES',
		description: 'You have the option for the top ' +
			'teams to play against each other in the final four ' +
			'rounds of your season. If you select \'No\', the winner ' +
			'of the league is the highest ranked team at the end ' +
			'of the final round of your season.',
	},
	finals_format: {
		title: 'FINALS FORMAT',
		description: 'Select how many teams play off ' +
			'in your leagues Primary Finals. 8 teams runs over ' +
			'4 weeks, 6 teams over 3 weeks, 4 teams over 2 week and ' +
			'2 teams is just a single round grand final match up. ' +
			'If there are enough teams in your league to run Secondary ' +
			'Finals of the same structure this will happen automatically as well.',
	},
	finals_start_round: {
		title: 'Finals start round',
		description: 'Select the round you would like your finals to commence'
	},
	bye_rounds: {
		title: 'BYE ROUNDS',
		description: 'This can be changed to Off ' +
			'to remove bye rounds from your leagues head ' +
			'to head fixtures. If set to On you will play ' +
			'against another team during these Bye Rounds',
	},
	coaches_box: {
		title: 'COACHES BOX',
		description: 'Turn on/off Coaches Box chat for the league',
	},
	play_times: {
		title: 'PLAY OPPONENTS',
		description: 'This can be changed so ' +
			'that teams can play each other more than ' +
			'once to work in conjunction with other ' +
			'configurable league settings like league size.',
	},
	squad_size: {
		title: 'Squad Size',
		description: 'The selected options indicate the Hookers - ' +
			'Middle - Edge - Halves - Centres - ' +
			'Winger/Fullback / Reserves users will have within their league. ' +
			'Based on the number options, this is how many players in the ' +
			'respective positions users will be required to select. ',
	},
	captains: {
		title: 'Captains',
		description: 'If Captains are enabled then a ' +
			'nominated player in each squad will receive double ' +
			'points for each round. Captains can be changed on ' +
			'a week to week basis. If there are no Captains then ' +
			'all players will receive equal weighted points.',
	},
	emergency: {
		title: 'Emergencies',
		description: 'Whether there are emergencies is ' +
			'up to the commissioner. If emergencies are turned ' +
			'on then the emergencies will cover a player who ' +
			'DNP with a matching position. The Emergency will ' +
			'be the lowest scoring player from that position on the bench.',
	},
	lockout: {
		title: 'Rolling Lockout',
		description: 'The commissioner can chose ' +
			'to have a rolling lockout where players can ' +
			'be traded/subbed etc until their respective game begins. ' +
			'If the commissioner chooses not to have a rolling ' +
			'lockout, the lockout will begin at the start of each round.',
	},
	trade_waivers: {
		title: 'Restricted Free Agents',
		description: 'If Restricted Free Agent system ' +
			'is turned on, when a Player is delisted, ' +
			'he will be classified as a Restricted Free ' +
			'Agent until the amount of time designated by ' +
			'the Commissioner has passed. After this time ' +
			'he will become a Free Agent. Any User can pick ' +
			'up any Free Agent at anytime. Any User can submit ' +
			'a request to pick up a Restricted Free Agent, ' +
			'however the User with the higher RFA Priority will ' +
			'get the Player if multiple Users select that Player',
	},
	waiver_period: {
		title: 'Restricted Free Agent Time Period',
		description: 'The Restricted Free Agent Time Period ' +
			'refers to the period of time that coaches have to ' +
			'submit their Restricted Free Agent Bid.',
	},
	waiver_order: {
		title: 'Restricted Free Agent Time Policy',
		description: 'If you decide to have Restricted ' +
			'Free Agents turned \'ON\' there are two order policies. ' +
			'The first being that the order is decided by reversing ' +
			'the ladder i.e. The Team that is positioned last on the ' +
			'ladder gets the first selection. The second waiver order ' +
			'policy is that if you had the 1st pick last week you will ' +
			'now have the last pick in the upcoming week. The week after ' +
			'that you will have the second last pick and so on.',
	},
	rfa_privacy: {
		title: 'Restricted Free Agents',
		description: 'If Private, when making RFA requests you can only see your ' +
			'own requests on the Active transactions page while pending. ' +
			'If Public, when making RFA requests you can see all other users RFA ' +
			'requests on the active transactions page while pending',
	},
	approve_trade: {
		title: 'Trade Approval Options',
		description: 'Manage the approval settings ' +
			'for Trades made between teams in your league.',
	}
};

const getSuffix = (j: number, k: number) => {
	let suffix = "th";

	if (j === 1 && k !== 11) {
		suffix = "st";
	}

	if (j === 2 && k !== 12) {
		suffix = "nd";
	}

	if (j === 3 && k !== 13) {
		suffix = "rd";
	}

	return suffix;
};

export const ordinalSuffixOf = (num: number) => {
	if (!parseInt(num, 10)) {
		return num;
	}
	const
		j = num % 10,
		k = num % 100;

	let suffix = getSuffix(j, k);

	return num + suffix;
};


// WEBPACK FOOTER //
// ./src/helpers/index.js